/**
 * WeaveIn.ai Platform - Notifications Page
 * Unified notifications view integrating all notification sources:
 * communications, documents, activities, enrollments, calendar,
 * telehealth, marketing & sales pipeline, workflows, referrals, system
 *
 * Uses the same GET /api/notifications endpoint and notification types
 * as NotificationBell/NotificationCenter - this is the full-page view.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Bell,
  Mail,
  MessageSquare,
  Phone,
  FileText,
  Calendar,
  Video,
  CheckCircle,
  AlertCircle,
  UserPlus,
  Workflow,
  Trash2,
  CheckCheck,
  Filter,
  ArrowRight,
  Clock,
  ClipboardList,
  RefreshCw,
  Inbox,
  Target,
  Rocket,
  Share2,
  Settings,
  Archive,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { apiClient } from '@/services/apiClient';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { toast } from 'sonner';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useBulkAcknowledge } from '@/hooks/useEcmReminders';

// ---------------------------------------------------------------------------
// Types — mirrors NotificationCenter/NotificationBell interfaces
// ---------------------------------------------------------------------------

interface Notification {
  id: number;
  user_id: number;
  organization_id: number | null;
  notification_type: string;
  title: string;
  message: string;
  link: string | null;
  icon: string | null;
  priority: string;
  read: boolean;
  read_at: string | null;
  archived: boolean;
  archived_at: string | null;
  related_member_id: number | null;
  related_workflow_id: number | null;
  related_task_id: number | null;
  related_referral_id: number | null;
  related_enrollment_id: number | null;
  related_activity_id: string | null;
  created_at: string;
  expires_at: string | null;
}

interface NotificationListResponse {
  notifications: Notification[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

// ---------------------------------------------------------------------------
// Categories — each defines which notification_type values it includes,
// plus optional server-side filters (link_contains, has_activity).
// ---------------------------------------------------------------------------

interface NotificationCategoryDef {
  id: string;
  label: string;
  icon: React.ReactNode;
  /** notification_type values to match (server-side via comma param) */
  types: string[];
  /** Additional query params for server-side filtering */
  queryParams?: Record<string, string | boolean>;
  /** Client-side filter for when we need link-based matching (sales) */
  clientFilter?: (n: Notification) => boolean;
}

const CATEGORIES: NotificationCategoryDef[] = [
  { id: 'all', label: 'All Notifications', icon: <Bell className="w-4 h-4" />, types: [] },
  { id: 'communication', label: 'Inbound Comms', icon: <Inbox className="w-4 h-4" />, types: ['communication'] },
  { id: 'sales', label: 'Marketing & Sales', icon: <Rocket className="w-4 h-4" />, types: ['task', 'activity', 'communication', 'calendar'],
    clientFilter: (n) => !!(n.link && n.link.includes('platform-sales')) || !!(n.related_activity_id),
  },
  { id: 'activity', label: 'Activities & Tasks', icon: <ClipboardList className="w-4 h-4" />, types: ['task', 'activity'],
    clientFilter: (n) => !(n.link && n.link.includes('platform-sales')),
  },
  { id: 'enrollment', label: 'Enrollments', icon: <UserPlus className="w-4 h-4" />, types: ['enrollment'] },
  { id: 'calendar', label: 'Calendar & Scheduling', icon: <Calendar className="w-4 h-4" />, types: ['calendar', 'event'] },
  { id: 'telehealth', label: 'Telehealth', icon: <Video className="w-4 h-4" />, types: ['telehealth'] },
  { id: 'document', label: 'Documents', icon: <FileText className="w-4 h-4" />, types: ['document'] },
  { id: 'referral', label: 'Referrals', icon: <Share2 className="w-4 h-4" />, types: ['referral'] },
  { id: 'workflow', label: 'Workflows', icon: <Workflow className="w-4 h-4" />, types: ['workflow'] },
  { id: 'system', label: 'System', icon: <AlertCircle className="w-4 h-4" />, types: ['system'] },
];

// ---------------------------------------------------------------------------
// Icon resolver — same logic as NotificationCenter for consistency
// ---------------------------------------------------------------------------

const getNotificationIcon = (notification: Notification) => {
  const iconName = notification.icon || notification.notification_type;
  const priorityClass =
    notification.priority === 'urgent' ? 'text-red-500' :
    notification.priority === 'high' ? 'text-orange-500' :
    'text-blue-500';
  const className = `h-5 w-5 ${priorityClass}`;

  switch (iconName) {
    case 'CheckCircle':
    case 'workflow_completion':
      return <CheckCircle className={className} />;
    case 'Mail':
    case 'communication':
      return <Mail className={className} />;
    case 'MessageSquare':
      return <MessageSquare className={className} />;
    case 'Phone':
      return <Phone className={className} />;
    case 'Workflow':
    case 'workflow':
      return <Workflow className={className} />;
    case 'UserPlus':
    case 'enrollment':
      return <UserPlus className={className} />;
    case 'AlertCircle':
    case 'system':
      return <AlertCircle className={className} />;
    case 'Calendar':
    case 'calendar':
    case 'event':
    case 'meeting':
      return <Calendar className={className} />;
    case 'FileText':
    case 'document':
    case 'note':
      return <FileText className={className} />;
    case 'Video':
    case 'telehealth':
      return <Video className={className} />;
    case 'ClipboardList':
    case 'activity':
      return <ClipboardList className={className} />;
    case 'Target':
    case 'stage_change':
      return <Target className={className} />;
    case 'ArrowRight':
      return <ArrowRight className={className} />;
    case 'referral':
      return <Share2 className={className} />;
    case 'task':
      // Sales activity tasks show target icon, others show checklist
      if (notification.link?.includes('platform-sales') || notification.related_activity_id) {
        return <Target className={className} />;
      }
      return <ClipboardList className={className} />;
    default:
      return <Bell className={className} />;
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface NotificationsPageProps {
  onNavigate?: (tab: string) => void;
}

export const NotificationsPage: React.FC<NotificationsPageProps> = ({ onNavigate }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const { acknowledge: bulkAckEcm, loading: bulkAckLoading } = useBulkAcknowledge();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [showRead, setShowRead] = useState(false);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});

  // -----------------------------------------------------------------------
  // Fetch
  // -----------------------------------------------------------------------

  const fetchNotifications = useCallback(async (pageNum: number = 1, append: boolean = false) => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page: pageNum,
        page_size: 30,
      };
      if (!showRead) {
        params.read = false;
      }

      // Apply server-side filters for active category
      if (activeCategory !== 'all') {
        const cat = CATEGORIES.find(c => c.id === activeCategory);
        if (cat && cat.types.length > 0) {
          params.notification_type = cat.types.join(',');
        }
        // Apply extra query params (e.g. link_contains for sales)
        if (cat?.queryParams) {
          Object.assign(params, cat.queryParams);
        }
      }

      const response = await apiClient.get<NotificationListResponse>('/api/notifications', { params });
      const data = response.data;

      // Apply client-side filter if the category defines one
      let items = data.notifications;
      if (activeCategory !== 'all') {
        const cat = CATEGORIES.find(c => c.id === activeCategory);
        if (cat?.clientFilter) {
          items = items.filter(cat.clientFilter);
        }
      }

      if (append) {
        setNotifications(prev => [...prev, ...items]);
      } else {
        setNotifications(items);
      }
      setTotal(data.total);
      setHasMore(data.has_more);
      setPage(pageNum);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [activeCategory, showRead]);

  // Fetch all unread for category counts
  const fetchCounts = useCallback(async () => {
    try {
      // Fetch all unread (up to 100) to compute category counts client-side
      const response = await apiClient.get<NotificationListResponse>('/api/notifications', {
        params: { page: 1, page_size: 100, read: false },
      });
      const all = response.data.notifications;
      const counts: Record<string, number> = {};
      counts['all'] = response.data.total;

      for (const cat of CATEGORIES) {
        if (cat.id === 'all') continue;
        let matching = all;
        if (cat.types.length > 0) {
          matching = matching.filter(n => cat.types.includes(n.notification_type));
        }
        if (cat.clientFilter) {
          matching = matching.filter(cat.clientFilter);
        }
        counts[cat.id] = matching.length;
      }
      setCategoryCounts(counts);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    fetchNotifications(1);
  }, [fetchNotifications]);

  // Auto-refresh notifications and counts every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchNotifications(1);
      fetchCounts();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchCounts]);

  // -----------------------------------------------------------------------
  // Actions — same endpoints as NotificationCenter/NotificationBell
  // -----------------------------------------------------------------------

  const markAsRead = async (notificationId: number) => {
    try {
      await apiClient.patch(`/api/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n =>
        n.id === notificationId ? { ...n, read: true, read_at: new Date().toISOString() } : n
      ));
    } catch {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiClient.patch('/api/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true, read_at: new Date().toISOString() })));
      setCategoryCounts(prev => {
        const zeroed: Record<string, number> = {};
        for (const k of Object.keys(prev)) zeroed[k] = 0;
        return zeroed;
      });
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Failed to mark all as read');
    }
  };

  const archiveNotification = async (notificationId: number, isCommunication: boolean = false) => {
    try {
      const params = isCommunication ? { params: { sync_email: true } } : {};
      await apiClient.patch(`/api/notifications/${notificationId}/archive`, undefined, params);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      fetchCounts();
      toast.success('Notification archived');
    } catch {
      toast.error('Failed to archive');
    }
  };

  const deleteNotification = async (notificationId: number, isCommunication: boolean = false) => {
    try {
      const params = isCommunication ? { params: { sync_email: true } } : {};
      await apiClient.delete(`/api/notifications/${notificationId}`, params);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      fetchCounts();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }
    if (notification.link && onNavigate) {
      // Extract the tab/path from the link, separating query params
      let path = notification.link.replace(/^\//, '');
      const [basePath, queryString] = path.split('?');
      // Store query params so the target page can read them (e.g., ?tab=activities)
      if (queryString) {
        const params = new URLSearchParams(queryString);
        const subTab = params.get('tab');
        if (subTab) {
          localStorage.setItem('navigate_sub_tab', subTab);
        }
        // Store step hint for enrollment detail pages (e.g., ?step=ECM_REASSESSMENT)
        const step = params.get('step');
        if (step) {
          localStorage.setItem('navigate_enrollment_step', step);
        }
      }

      // Handle deep-link patterns that need ID extraction for SPA routing
      const enrollmentMatch = basePath.match(/^enrollments\/(\d+)$/);
      if (enrollmentMatch) {
        localStorage.setItem('selectedEnrollmentId', enrollmentMatch[1]);
        onNavigate('enrollments-view');
        return;
      }

      // Navigate using just the base tab name for SPA routing
      onNavigate(basePath);
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  const getDisplayType = (n: Notification): string => {
    if (n.link?.includes('platform-sales') || n.related_activity_id) {
      return 'sales';
    }
    return n.notification_type;
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Bell className="w-6 h-6" />
            {t('notifications.title', 'Notifications')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {categoryCounts['all'] || 0} unread &middot; {total} {showRead ? 'total' : 'unread'} showing
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={showRead ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setShowRead(!showRead)}
          >
            {showRead ? 'Showing All' : 'Unread Only'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={markAllAsRead}
            disabled={!categoryCounts['all']}
          >
            <CheckCheck className="w-4 h-4 mr-1" />
            Mark All Read
          </Button>
          {(activeCategory === 'enrollment' || activeCategory === 'all') && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const result = await bulkAckEcm({ reminder_type: 'follow_up_contact' });
                if (result) {
                  toast.success(result.message);
                  fetchNotifications(1);
                  fetchCounts();
                } else {
                  toast.error('Failed to acknowledge ECM reminders');
                }
              }}
              disabled={bulkAckLoading}
              className="text-green-700 border-green-200 hover:bg-green-50"
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              Ack ECM Reminders
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchNotifications(1)}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {onNavigate && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                localStorage.setItem('preferences_default_tab', 'general');
                onNavigate('user-preferences');
              }}
              title="Notification preferences"
            >
              <Settings className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-6">
        {/* Category Sidebar */}
        <div className="w-64 flex-shrink-0">
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Categories
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {CATEGORIES.map(cat => {
                  const count = categoryCounts[cat.id] || 0;
                  const isActive = activeCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setActiveCategory(cat.id)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                        isActive
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <span className={isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}>
                        {cat.icon}
                      </span>
                      <span className="flex-1">{cat.label}</span>
                      {count > 0 && (
                        <Badge variant={isActive ? 'primary' : 'default'} size="sm">
                          {count}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Notifications List */}
        <div className="flex-1 min-w-0">
          {loading && notifications.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-3 animate-spin" />
                <p className="text-gray-500">Loading notifications...</p>
              </CardContent>
            </Card>
          ) : notifications.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Bell className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  {activeCategory === 'all' ? "You're all caught up!" : 'No notifications'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {activeCategory !== 'all' &&
                    `No ${CATEGORIES.find(c => c.id === activeCategory)?.label.toLowerCase()} notifications`}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {notifications.map(notification => (
                <Card
                  key={notification.id}
                  className={`transition-all hover:shadow-md ${
                    !notification.read
                      ? 'border-l-4 border-l-blue-500 bg-blue-50/30 dark:bg-blue-900/10'
                      : 'border-l-4 border-l-transparent'
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification)}
                      </div>

                      {/* Content */}
                      <div
                        className={`flex-1 min-w-0 ${notification.link ? 'cursor-pointer' : ''}`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${!notification.read ? 'font-semibold' : 'font-medium'} text-gray-900 dark:text-gray-100`}>
                              {notification.title}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                              {notification.message}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {notification.priority === 'urgent' && (
                              <Badge variant="destructive" size="sm">Urgent</Badge>
                            )}
                            {notification.priority === 'high' && (
                              <Badge variant="warning" size="sm">High</Badge>
                            )}
                            <Badge variant="default" size="sm" className="capitalize text-xs">
                              {getDisplayType(notification).replace('_', ' ')}
                            </Badge>
                          </div>
                        </div>

                        {/* Meta row */}
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {fmt.relative(notification.created_at)}
                          </span>
                          {notification.related_member_id && (
                            <span className="text-xs text-gray-400">Member #{notification.related_member_id}</span>
                          )}
                          {notification.link && (
                            <span className="text-xs text-blue-500 flex items-center gap-1 hover:underline">
                              <ArrowRight className="w-3 h-3" />
                              View
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!notification.read && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
                            title="Mark as read"
                            className="h-9 px-3 text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
                          >
                            <CheckCircle className="w-4.5 h-4.5 mr-1.5" />
                            Read
                          </Button>
                        )}
                        {notification.notification_type === 'communication' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); archiveNotification(notification.id, true); }}
                            title="Archive"
                            className="h-9 px-3 text-gray-500 border-gray-200 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-300"
                          >
                            <Archive className="w-4.5 h-4.5 mr-1.5" />
                            Archive
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); deleteNotification(notification.id, notification.notification_type === 'communication'); }}
                          title="Delete"
                          className="h-9 px-3 text-gray-500 border-gray-200 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                        >
                          <Trash2 className="w-4.5 h-4.5 mr-1.5" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Load More */}
              {hasMore && (
                <div className="text-center py-4">
                  <Button
                    variant="outline"
                    onClick={() => fetchNotifications(page + 1, true)}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
